/**
 * WorkedWith — "Brands & organizations" collaboration showcase.
 *
 * Renders the COMPANIES list as flip cards on desktop (front/back swapped on
 * hover or focus) and as an accordion list on mobile (tap to expand the
 * description). Entrance animation is gated by useGlobalReveal.
 */
import { useState } from "react";
import "../ComponentsStyling/WorkedWith.css";
import { useGlobalReveal } from "../utils/useGlobalReveal";

/* Organizations worked with, newest first. `tag` marks the current role and
   `scope` is a display label. The card's "01", "02" … come off the ARRAY
   INDEX, not a field — they were hand-written per entry, which meant adding
   an engagement at the top silently renumbered nothing and left two cards
   claiming the same position.

   `w` is the logo's own display width, feeding `--lw` in the stylesheet:
   these marks run from a 5.6:1 wordmark to a near-square lock-up, so one
   shared width made the wide ones render a third the height of the tall ones.
   Each width is chosen so every mark occupies roughly the same optical area
   on the card (~9,500px²). */
const COMPANIES = [
  {
    key: "shabakat-ksa",
    // Spelled to match the wordmark in the logo file, which reads
    // "Shabakkat | KSA" — the name sits directly under that artwork on the
    // card, so the two cannot disagree.
    name: "Shabakkat KSA",
    logo: "/images/WorkedWith/shabakatKSA.png",
    // 400x72 is the widest mark in the set at 5.6:1 — at the 160–210px the
    // others use it would stand only 38px tall.
    w: 230,
    role: "Software Engineer Intern",
    period: "2026 – Present",
    scope: "Corporate · Internship",
    tag: "Current",
    desc: "Joined Shabakkat KSA as a Software Engineer Intern, working alongside the engineering team on the company's software development. First professional software engineering role.",
  },
  {
    key: "ieee-cis",
    name: "IEEE CIS",
    logo: "/images/WorkedWith/ieeecis.png",
    w: 160,
    role: "Webmaster & Web Team Mentor",
    period: "2025 – 2026",
    scope: "Academic · IEEE",
    tag: null,
    // The longest entry by some way. The closing clause ("while ensuring
    // performance, reliability, and an exceptional user experience") was the
    // two lines that pushed it past what the card can hold, and it claimed
    // nothing the rest of the paragraph had not already earned.
    desc: "Led the web presence of IEEE CIS University of Jordan for two consecutive years. In 2025, served as Webmaster and Project Mentor, guiding web initiatives and supporting team members throughout development. In 2026, independently managed the organization's entire web ecosystem, taking full ownership of design, development, deployment, and maintenance. Built and maintained scalable React-based solutions serving hundreds of monthly users and supporting major events with over 1,100+ visitors.",
  },
  {
    key: "ieee-jordan",
    name: "IEEE Jordan Section",
    logo: "/images/WorkedWith/sectionieee.png",
    w: 129,
    role: "Frontend Developer",
    period: "2025",
    scope: "Academic · Dashboard",
    tag: null,
    desc: "Developed a real-time competition dashboard for IEEE hackathons, enabling judges, participants, and organizers to monitor rankings, scores, and event progress efficiently. Built responsive and interactive interfaces focused on usability, performance, and live data visualization.",
  },
  {
    key: "smartcell",
    name: "SmartCell",
    logo: "/images/WorkedWith/smartCellLogoWTxt.png",
    w: 86,
    role: "Freelance Web Developer",
    period: "2025",
    scope: "Corporate · Freelance",
    tag: null,
    desc: "Designed and developed a complete corporate website for Smart Cell from the ground up. Delivered a responsive, high-performance platform using HTML, CSS, and JavaScript, transforming business requirements into a modern digital experience while ensuring maintainability, scalability, and a strong user experience.",
  },
  {
    key: "TedxUj",
    name: "TEDx University of Jordan",
    logo: "/images/WorkedWith/TedxUj.PNG",
    w: 210,
    role: "Sole Webmaster & Frontend Developer",
    period: "2026 – Present",
    scope: "Global Event",
    tag: null,
    desc: "Served as the sole Webmaster and Frontend Developer for TEDx University of Jordan, leading all web-related initiatives and maintaining the organization's digital presence. Designed, developed, and managed the official event website, delivering a modern, responsive, and engaging platform that supported event promotion, speaker showcases, attendee engagement, and organizational communications.",
  },
  {
    key: "iise",
    name: "IISE UJ",
    logo: "/images/WorkedWith/IISELOGO.PNG",
    w: 139,
    role: "Sole Web Developer",
    period: "2026",
    scope: "Academic · Events",
    tag: null,
    desc: "Served as the sole Web Developer for IISE University of Jordan, owning the complete web development process from design to deployment. Developed and maintained responsive web solutions supporting organizational activities and events while ensuring performance, accessibility, and a seamless user experience across devices.",
  },
  {
    key: "ai-week",
    name: "AI Week",
    logo: "/images/WorkedWith/AiWeek.png",
    w: 187,
    role: "Lead Frontend Developer",
    period: "2025",
    scope: "Technology Event",
    tag: null,
    desc: "Led frontend development and web operations for AI Week, a large-scale AI event attracting over 600 attendees. Collaborated with organizers and technical teams to deliver a production-ready platform for schedules, workshops, hackathons, and event activities, ensuring a smooth and engaging experience for attendees, speakers, sponsors, and partners.",
  },
  {
    key: "ai-expo",
    name: "AI Expo 2026",
    logo: "/images/WorkedWith/aiExpoBlack.png",
    w: 182,
    role: "Sole Webmaster & Frontend Developer",
    period: "2026 – Present",
    scope: "National Event",
    tag: null,
    // Restored without the "1,200+ attendees" figure, which was dropped from
    // this project elsewhere on the site. While it was commented out this card
    // was the one entry that flipped to an empty panel on hover.
    desc: "Served as the sole Webmaster and Frontend Developer for AI Expo 2026, one of Jordan's largest AI-focused events, held under the sponsorship of the Ministry of Digital Economy & Entrepreneurship. Led the complete web development lifecycle from planning and architecture through to deployment and maintenance, building a scalable React platform covering registrations, ticketing, schedules, and event information.",
  },
];

export default function WorkedWith() {
  const [ref, inView] = useGlobalReveal({ threshold: 0.13, once: true });
  const [active, setActive] = useState(null);    // key of the flipped desktop card
  const [expanded, setExpanded] = useState(null); // key of the open mobile card

  return (
    <section className="ww" id="workedwith" ref={ref}>
      <div className="ww__dots" aria-hidden="true" />
      <div className="ww__blob--a" aria-hidden="true" />
      <div className="ww__blob--b" aria-hidden="true" />

      {/* Section header: eyebrow, headline and summary */}
      <div className={`ww__head ${inView ? "ww__head--in" : ""}`}>
        <div className="ww__eyebrow">
          <span className="ww__ey-num">05</span>
          <span className="ww__ey-line" aria-hidden="true" />
          <span className="ww__ey-label">Worked With</span>
        </div>

        <div className="ww__headline-row">
          <h2 className="ww__h2">
            <span className="ww__h2-a" style={{ "--d": "0s" }}>Brands &amp;</span>
            <span className="ww__h2-b" style={{ "--d": "0.07s" }}>
              <em className="ww__h2-em">organizations</em>
            </span>
            <span className="ww__h2-c" style={{ "--d": "0.13s" }}>I've built for.</span>
          </h2>

          <div className="ww__head-right" style={{ "--d": "0.14s" }}>
            <p className="ww__head-desc">
              {COMPANIES.length} collaborations across academic,<br />
              corporate &amp; freelance contexts.
            </p>
            <div className="ww__head-tags">
              {["Academic", "Corporate", "Freelance"].map((t) => (
                <span key={t} className="ww__head-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`ww__sep ${inView ? "ww__sep--in" : ""}`} aria-hidden="true" />

      {/* Desktop flip-card grid */}
      <div className={`ww__grid ${inView ? "ww__grid--in" : ""}`}>
        {COMPANIES.map((c, i) => {
          const isActive = active === c.key;
          const num = String(i + 1).padStart(2, "0");
          return (
            <div
              key={c.key}
              className={`ww__card ${isActive ? "ww__card--active" : ""}`}
              style={{ "--i": i }}
              onMouseEnter={() => setActive(c.key)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(c.key)}
              onBlur={() => setActive(null)}
              tabIndex={0}
              role="group"
              aria-label={`${c.name} — ${c.role}`}
            >
              {/* Card front: logo, name and role (hidden from AT while flipped) */}
              <div className="ww__front" aria-hidden={isActive}>
                <div className="ww__front-top">
                  <span className="ww__front-num">{num}</span>
                  <span className="ww__front-scope">{c.scope}</span>
                  {c.tag && <span className="ww__front-tag">{c.tag}</span>}
                </div>
                <div className="ww__logo-wrap">
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="ww__logo"
                    style={{ "--lw": `${c.w}px` }}
                    loading="lazy"
                  />
                </div>
                <div className="ww__front-bottom">
                  <h3 className="ww__front-name">{c.name}</h3>
                  <span className="ww__front-role">{c.role}</span>
                </div>
              </div>

              {/* Card back: period plus full description (revealed on flip).
                  The hatch texture, corner glow and ghosted logo that used to
                  sit in here are gone — they only competed with the copy, and
                  losing the logo gave the description the room it needed to
                  stop truncating mid-sentence. */}
              <div className="ww__back" aria-hidden={!isActive}>
                <div className="ww__back-inner">
                  <div className="ww__back-top">
                    <div className="ww__back-meta">
                      <span className="ww__back-num">{num}</span>
                      <span className="ww__back-period">{c.period}</span>
                    </div>
                    {c.tag && <span className="ww__back-tag">{c.tag}</span>}
                  </div>
                  <h3 className="ww__back-name">{c.name}</h3>
                  <span className="ww__back-role">{c.role}</span>
                  <div className="ww__back-rule" aria-hidden="true" />
                  <p className="ww__back-desc">{c.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile accordion list (shown on small screens via CSS) */}
      <div className={`ww__mob-list ${inView ? "ww__mob-list--in" : ""}`}>
        {COMPANIES.map((c, i) => {
          const isOpen = expanded === c.key;
          const num = String(i + 1).padStart(2, "0");
          return (
            <div
              key={c.key}
              className={`ww__mob-card ${isOpen ? "ww__mob-card--open" : ""}`}
              style={{ "--mi": i }}
            >
              {/* Always-visible row: logo, name, role and expand toggle */}
              <div className="ww__mob-head">
                <div className="ww__mob-head-left">
                  <div className="ww__mob-logo-wrap">
                    <img src={c.logo} alt={c.name} className="ww__mob-logo" loading="lazy" />
                  </div>
                  <div className="ww__mob-info">
                    <div className="ww__mob-top">
                      <span className="ww__mob-num">{num}</span>
                      <span className="ww__mob-scope">{c.scope}</span>
                      {c.tag && <span className="ww__mob-tag">{c.tag}</span>}
                    </div>
                    <h3 className="ww__mob-name">{c.name}</h3>
                    <span className="ww__mob-role">{c.role}</span>
                    <span className="ww__mob-period">{c.period}</span>
                  </div>
                </div>

                {/* Toggles the description open/closed. Skipped when an entry
                    has no description, so the toggle can never expand into an
                    empty panel. */}
                {c.desc && (
                  <button
                    className={`ww__mob-toggle ${isOpen ? "ww__mob-toggle--open" : ""}`}
                    onClick={() => setExpanded(isOpen ? null : c.key)}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? "Collapse" : "Read more about"} ${c.name}`}
                  >
                    <svg viewBox="0 0 10 6" fill="none" aria-hidden="true">
                      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Expandable description */}
              {c.desc && (
                <div className="ww__mob-body" aria-hidden={!isOpen}>
                  <div className="ww__mob-rule" aria-hidden="true" />
                  <p className="ww__mob-desc">{c.desc}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}